import type {
  GranuleStatuses,
  SubroleGranules,
} from '@b-stock/authorization-core'
import { makePermissionHelpers } from '@b-stock/fe-core/bstock-next/helpers'

// Enum for Permission Granules
export enum PermissionGranule {
  FEBP_LISTING_DETAIL_MORE_FROM_SELLER = 'FEBP_LISTING_DETAIL_MORE_FROM_SELLER',
  FEBP_LISTING_DETAIL_EDIT_SHIP_TO = 'FEBP_LISTING_DETAIL_EDIT_SHIP_TO',
  FEBP_LISTING_DETAIL_BID = 'FEBP_LISTING_DETAIL_BID',
  FEBP_LISTING_DETAIL_AUCTION_STATS = 'FEBP_LISTING_DETAIL_AUCTION_STATS',
  FEBP_SAVED_SEARCHES_READ = 'FEBP_SAVED_SEARCHES_READ',
  FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS = 'FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS',
  FEBP_LISTING_TERMS_ACCEPT = 'FEBP_LISTING_TERMS_ACCEPT',
  FEBP_ORDER_DOCUMENTS_UPLOAD = 'FEBP_ORDER_DOCUMENTS_UPLOAD',
  // FEAP (Front-End Accounts Portal) permission granules for My Account page tiles
  FEAP_PERSONAL_SETTINGS_VIEW = 'FEAP_PERSONAL_SETTINGS_VIEW',
  FEAP_BUSINESS_SETTINGS_VIEW = 'FEAP_BUSINESS_SETTINGS_VIEW',
  FEAP_PAYMENT_METHODS_VIEW = 'FEAP_PAYMENT_METHODS_VIEW',
  FEAP_NOTIFICATION_SETTINGS_VIEW = 'FEAP_NOTIFICATION_SETTINGS_VIEW',
  FEAP_MARKETPLACE_ELIGIBILITY_VIEW = 'FEAP_MARKETPLACE_ELIGIBILITY_VIEW',
  FEAP_ACCOUNT_USERS_VIEW = 'FEAP_ACCOUNT_USERS_VIEW',
  FEAP_ACCOUNT_USERS_MANAGE = 'FEAP_ACCOUNT_USERS_MANAGE',
}

// Permission Granule Statuses
const permissionGranuleStatuses: GranuleStatuses<PermissionGranule> = {
  FEBP_LISTING_DETAIL_MORE_FROM_SELLER: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED'],
    service: ['ACTIVE'],
  },
  FEBP_LISTING_DETAIL_EDIT_SHIP_TO: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED'],
    service: ['ACTIVE'],
  },
  FEBP_LISTING_DETAIL_BID: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED'],
    service: ['ACTIVE'],
  },
  FEBP_LISTING_DETAIL_AUCTION_STATS: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED'],
    service: ['ACTIVE'],
  },
  FEBP_SAVED_SEARCHES_READ: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED', 'READ_ONLY'],
    service: ['ACTIVE'],
  },
  FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS: {
    seller: ['ACTIVE', 'INTENDED', 'SUSPENDED', 'READ_ONLY'],
    service: ['ACTIVE'],
  },
  FEBP_LISTING_TERMS_ACCEPT: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED'],
  },
  // Active accounts only, as the order service accepts document uploads and
  // deletes from buyers.
  FEBP_ORDER_DOCUMENTS_UPLOAD: {
    buyer: ['ACTIVE'],
  },
  // FEAP: person-scoped granules
  FEAP_PERSONAL_SETTINGS_VIEW: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED', 'READ_ONLY'],
    service: ['ACTIVE'],
  },
  FEAP_NOTIFICATION_SETTINGS_VIEW: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED', 'READ_ONLY'],
    service: ['ACTIVE'],
  },
  // FEAP: account-scoped granules
  FEAP_BUSINESS_SETTINGS_VIEW: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED', 'READ_ONLY'],
    service: ['ACTIVE'],
  },
  FEAP_PAYMENT_METHODS_VIEW: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED', 'READ_ONLY'],
    service: ['ACTIVE'],
  },
  FEAP_MARKETPLACE_ELIGIBILITY_VIEW: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED', 'READ_ONLY'],
    service: ['ACTIVE'],
  },
  FEAP_ACCOUNT_USERS_VIEW: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED', 'READ_ONLY'],
    service: ['ACTIVE'],
  },
  // Deliberately no READ_ONLY: a read-only buyer may look at the roster
  // (FEAP_ACCOUNT_USERS_VIEW above) but must not invite, change roles or
  // remove people. Same shape the other buyer write granules use
  // (FEBP_LISTING_DETAIL_BID, FEBP_LISTING_TERMS_ACCEPT).
  FEAP_ACCOUNT_USERS_MANAGE: {
    buyer: ['ACTIVE', 'INTENDED', 'SUSPENDED'],
    service: ['ACTIVE'],
  },
}

// FEAP granules granted to all authenticated buyer subroles
const FEAP_BUYER_GRANULES = [
  PermissionGranule.FEAP_PERSONAL_SETTINGS_VIEW,
  PermissionGranule.FEAP_BUSINESS_SETTINGS_VIEW,
  PermissionGranule.FEAP_PAYMENT_METHODS_VIEW,
  PermissionGranule.FEAP_NOTIFICATION_SETTINGS_VIEW,
  PermissionGranule.FEAP_MARKETPLACE_ELIGIBILITY_VIEW,
] as const

// Everything a buyer on the account can see today, minus the owner-only
// FEAP_ACCOUNT_USERS_VIEW / FEAP_ACCOUNT_USERS_MANAGE. ACCOUNT_OWNER spreads
// this too and then adds the users granules, so the two lists can never
// silently drift apart.
const BUYER_MEMBER_GRANULES = [
  PermissionGranule.FEBP_LISTING_DETAIL_MORE_FROM_SELLER,
  PermissionGranule.FEBP_LISTING_DETAIL_EDIT_SHIP_TO,
  PermissionGranule.FEBP_LISTING_DETAIL_BID,
  PermissionGranule.FEBP_LISTING_DETAIL_AUCTION_STATS,
  PermissionGranule.FEBP_SAVED_SEARCHES_READ,
  PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
  PermissionGranule.FEBP_LISTING_TERMS_ACCEPT,
  PermissionGranule.FEBP_ORDER_DOCUMENTS_UPLOAD,
  ...FEAP_BUYER_GRANULES,
] as const

// Subroles and their Permissions
const subroles: SubroleGranules<PermissionGranule> = {
  /**
   * The key is the SCREAMING_SNAKE form of the subrole string:
   * `PermissionInspector` resolves a person's subroles as
   * `subroles[subrole.toUpperCase()]`, so a token carrying `buyer_member`
   * lands here.
   *
   * Without this entry, an explicit non-owner member would see an empty My
   * Account hub the moment FP-3002/BP-3003 start writing real member subroles
   * (today every person defaults to `account_owner`, which is why the hub
   * renders for ordinary buyers at all).
   */
  BUYER_MEMBER: BUYER_MEMBER_GRANULES,
  BACKEND: [
    PermissionGranule.FEBP_LISTING_DETAIL_MORE_FROM_SELLER,
    PermissionGranule.FEBP_LISTING_DETAIL_EDIT_SHIP_TO,
    PermissionGranule.FEBP_LISTING_DETAIL_BID,
    PermissionGranule.FEBP_LISTING_DETAIL_AUCTION_STATS,
    PermissionGranule.FEBP_SAVED_SEARCHES_READ,
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
    ...FEAP_BUYER_GRANULES,
  ],
  ADMIN: [
    PermissionGranule.FEBP_LISTING_DETAIL_MORE_FROM_SELLER,
    PermissionGranule.FEBP_LISTING_DETAIL_EDIT_SHIP_TO,
    PermissionGranule.FEBP_LISTING_DETAIL_BID,
    PermissionGranule.FEBP_LISTING_DETAIL_AUCTION_STATS,
    PermissionGranule.FEBP_SAVED_SEARCHES_READ,
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
    ...FEAP_BUYER_GRANULES,
  ],
  DEVELOPER: [
    PermissionGranule.FEBP_LISTING_DETAIL_MORE_FROM_SELLER,
    PermissionGranule.FEBP_LISTING_DETAIL_EDIT_SHIP_TO,
    PermissionGranule.FEBP_LISTING_DETAIL_BID,
    PermissionGranule.FEBP_LISTING_DETAIL_AUCTION_STATS,
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
    ...FEAP_BUYER_GRANULES,
  ],
  CUSTOMER_SERVICE_MANAGER: [
    PermissionGranule.FEBP_LISTING_DETAIL_AUCTION_STATS,
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
  ],
  CUSTOMER_SERVICE_REPRESENTATIVE: [
    PermissionGranule.FEBP_LISTING_DETAIL_MORE_FROM_SELLER,
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
  ],
  ACCOUNT_OWNER: [
    ...BUYER_MEMBER_GRANULES,
    // Owner-only: managing the people on the account is an owner action, so
    // only owners get the tile and the page (the backend read itself is open
    // to any member — this is a UI/granule choice, see FP-2995).
    PermissionGranule.FEAP_ACCOUNT_USERS_VIEW,
    // Owner-only, and separate from VIEW on purpose. FP-3006 gives members a
    // read-only view of the roster; the day that ships, VIEW moves to
    // BUYER_MEMBER and MANAGE stays here, so the row actions and the invite
    // button never have to be re-gated (FP-3002).
    PermissionGranule.FEAP_ACCOUNT_USERS_MANAGE,
  ],
  // A person acting on their own record. accounts-portal grants SELF the
  // person-scoped settings; mirror that here so a member whose only subrole is
  // `self` still reaches their personal and notification settings.
  SELF: [
    PermissionGranule.FEAP_PERSONAL_SETTINGS_VIEW,
    PermissionGranule.FEAP_NOTIFICATION_SETTINGS_VIEW,
  ],
  UNAUTHENTICATED: [
    PermissionGranule.FEBP_LISTING_DETAIL_MORE_FROM_SELLER,
    PermissionGranule.FEBP_LISTING_DETAIL_AUCTION_STATS,
  ],
  MEDIATION: [
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
  ],
  FINANCE: [PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS],
  SALES: [PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS],
  ACCOUNT_MANAGER: [
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
  ],
  LISTINGS_MANAGER: [
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
  ],
  LISTINGS_SPECIALIST: [
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
  ],
  WAREHOUSE_MANAGER: [
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
  ],
  LIMITED_ACCESS_USER: [
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
  ],
}

const { usePermission, hasPermission } = makePermissionHelpers(
  permissionGranuleStatuses,
  subroles
)

export { usePermission, hasPermission }
